import React, { useState, useEffect, useCallback } from 'react';
import { CircularProgress } from '@mui/material';
import GoogleMapReact from 'google-map-react';
import MapMarker from '../../container/MapsBar/MapMarker';
import axios from 'axios';
import { APIURL, GOOGLEMAPSAPIKEY } from '../../config';
import moment from 'moment';
import { getSessionAttendance } from './shiftStatus';
import './ShiftMap.css';

export default function ShiftMap(props) {
    const [isLoading, setIsLoading] = useState(true);
    const [shifts, setShifts] = useState([]);
    const [companyLocation, setCompanyLocation] = useState({ lat: 40.754885, lng: -73.984396 });
    const [mapRef, setMapRef] = useState(null);
    const [mapsRef, setMapsRef] = useState(null);

    useEffect(() => {
        fetchCompanyLocation();
        fetchShifts();
    }, []);

    const fetchCompanyLocation = async () => {
        try {
            const companyRes = await axios.get(APIURL + '/company', {
                params: { comp_id: props.comp_id },
            });
            const companyData = companyRes?.data[0];
            if (!companyData) return;

            const addr = [
                companyData.loc_addr_line1,
                companyData.loc_addr_line2,
                companyData.loc_city,
                companyData.loc_state,
                companyData.loc_zip,
            ].filter(Boolean).join(' ');

            const mapsRes = await axios.get(APIURL + '/maps', { params: { input: addr } });
            const placeId = mapsRes?.data?.predictions?.[0]?.place_id;
            if (!placeId) return;

            const detailsRes = await axios.get(APIURL + '/maps/details', { params: { place_id: placeId } });
            const loc = detailsRes?.data?.result?.geometry?.location;
            if (loc?.lat && loc?.lng) {
                setCompanyLocation({ lat: loc.lat, lng: loc.lng });
            }
        } catch (error) {
            console.error('Error fetching company location:', error);
        }
    };

    const fetchShifts = async () => {
        try {
            setIsLoading(true);
            const today = moment().format('YYYY-MM-DD');

            // Fetch today's schedules
            const schedulesRes = await axios.get(
                APIURL + '/master_schedule/company/' + props.comp_id,
                { params: { startDate: today, endDate: today } }
            );

            const allSchedules = schedulesRes.data || [];
            if (allSchedules.length === 0) {
                setShifts([]);
                setIsLoading(false);
                return;
            }

            // Fetch sessions for all of today's schedules, since clock-ins decide which are active
            const scheduleIds = allSchedules.map(s => s.schedule_id);
            const sessionsRes = await axios.post(APIURL + '/sessions/schedule', { scheduleIds });
            const sessions = sessionsRes.data || [];

            const now = moment();
            const todaySchedules = allSchedules.filter(schedule => {
                const attendance = sessions.filter(s => s.schedule_id === schedule.schedule_id).map(getSessionAttendance);
                // Someone is clocked in, even before the scheduled start
                if (attendance.some(a => a.checkedIn && !a.checkedOut)) return true;
                // Started, not yet ended, and no one has clocked in (or out, if they left early) yet
                const inWindow = moment(schedule.start_time).isSameOrBefore(now) && moment(schedule.end_time).isSameOrAfter(now);
                return inWindow && !attendance.some(a => a.checkedIn);
            });

            if (todaySchedules.length === 0) {
                setShifts([]);
                setIsLoading(false);
                return;
            }

            // Fetch resources for names
            const resourcesRes = await axios.get(APIURL + '/resources/company/' + props.comp_id);
            const resources = resourcesRes.data || [];

            // Fetch employees for names
            const employeesRes = await axios.get(APIURL + '/employees', { params: { comp_id: props.comp_id } });
            const employees = employeesRes.data || [];

            // Fetch customer locations
            const customersRes = await axios.get(APIURL + '/customers', { params: { com_id: props.comp_id } });
            const customers = customersRes.data || [];

            // Build shift data with check-in status
            const shiftData = todaySchedules.map(schedule => {
                const scheduleSessions = sessions.filter(s => s.schedule_id === schedule.schedule_id);
                const hasCheckin = scheduleSessions.some(s => getSessionAttendance(s).checkedIn);
                const resource = resources.find(r => r.resource_id === schedule.resource_id);
                const assignedEmployees = (schedule.employee_ids || [])
                    .map(id => employees.find(e => e.id === id))
                    .filter(Boolean);
                const assignedCustomers = scheduleSessions
                    .map(s => customers.find(c => c.cus_id === s.customer_id))
                    .filter(Boolean);

                // Try to get location from the first customer's project location
                let lat = null, lng = null;
                for (const cust of assignedCustomers) {
                    if (cust.cus_lat && cust.cus_lon) {
                        lat = parseFloat(cust.cus_lat);
                        lng = parseFloat(cust.cus_lon);
                        break;
                    }
                    if (cust.projects && cust.projects.length > 0) {
                        const proj = cust.projects.find(p => p.pro_is_active);
                        if (proj?.loc_lat && proj?.loc_lon) {
                            lat = parseFloat(proj.loc_lat);
                            lng = parseFloat(proj.loc_lon);
                            break;
                        }
                    }
                }

                return {
                    schedule_id: schedule.schedule_id,
                    title: schedule.schedule_name,
                    start_time: schedule.start_time,
                    end_time: schedule.end_time,
                    hasCheckin,
                    missingCheckin: !hasCheckin,
                    resourceName: resource?.resource_name || '',
                    employeeNames: assignedEmployees.map(e => e.employee_name).join(', '),
                    customerNames: assignedCustomers.map(c => c.cus_name).join(', '),
                    lat,
                    lng,
                };
            });

            // Sort: missing check-in first
            shiftData.sort((a, b) => (b.missingCheckin ? 1 : 0) - (a.missingCheckin ? 1 : 0));

            setShifts(shiftData);
        } catch (error) {
            console.error('Error fetching shift data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const missingCount = shifts.filter(s => s.missingCheckin).length;

    const handleApiLoaded = useCallback(({ map, maps }) => {
        setMapRef(map);
        setMapsRef(maps);
        fitBounds(map, maps);
    }, [shifts]);

    const fitBounds = (map, maps) => {
        if (!map || !maps) return;
        const markable = shifts.filter(s => s.lat && s.lng);
        if (markable.length > 1) {
            const bounds = new maps.LatLngBounds();
            markable.forEach(s => bounds.extend(new maps.LatLng(s.lat, s.lng)));
            map.fitBounds(bounds);
        } else if (markable.length === 1) {
            map.setCenter({ lat: markable[0].lat, lng: markable[0].lng });
            map.setZoom(13);
        }
    };

    const handleSidebarClick = (shift) => {
        if (mapRef && mapsRef && shift.lat && shift.lng) {
            mapRef.setCenter({ lat: shift.lat, lng: shift.lng });
            mapRef.setZoom(15);
        }
    };

    if (isLoading) {
        return (
            <div className="shift-map-loading">
                <CircularProgress size={32} />
            </div>
        );
    }

    return (
        <div className="shift-map-container">
            <div className="shift-map-header">
                <div className="shift-map-header-title">
                    <i className="fas fa-map-marked-alt" style={{ color: '#3fa7d6' }}></i>
                    Active Shifts
                    {missingCount > 0 && (
                        <span className="shift-map-header-badge">
                            {missingCount} missing check-in
                        </span>
                    )}
                </div>
            </div>
            <div className="shift-map-body">
                <div className="shift-map-map">
                    {shifts.length === 0 ? (
                        <div className="shift-map-empty">
                            <i className="fas fa-map" style={{ fontSize: 32, color: '#ccc' }}></i>
                            <span>No active shifts right now</span>
                        </div>
                    ) : (
                        <GoogleMapReact
                            bootstrapURLKeys={{ key: GOOGLEMAPSAPIKEY }}
                            defaultCenter={companyLocation}
                            defaultZoom={10}
                            options={{
                                fullscreenControl: false,
                                mapTypeControl: false,
                            }}
                            onGoogleApiLoaded={handleApiLoaded}
                            yesIWantToUseGoogleMapApiInternals
                        >
                            {shifts
                                .filter(s => s.lat && s.lng)
                                .map((shift) => (
                                    <MapMarker
                                        key={shift.schedule_id}
                                        lat={shift.lat}
                                        lng={shift.lng}
                                        taskTitle={shift.title}
                                        empName={shift.employeeNames || 'Unassigned'}
                                        location={shift.customerNames}
                                        onClick={() => handleSidebarClick(shift)}
                                    />
                                ))
                            }
                        </GoogleMapReact>
                    )}
                </div>
                {shifts.length > 0 && (
                    <div className="shift-map-sidebar">
                        {shifts.map((shift) => (
                            <div
                                key={shift.schedule_id}
                                className="shift-map-sidebar-item"
                                onClick={() => handleSidebarClick(shift)}
                            >
                                <div className="shift-map-sidebar-item-title">{shift.title}</div>
                                <div className="shift-map-sidebar-item-sub">
                                    {moment(shift.start_time).format('h:mm A')} – {moment(shift.end_time).format('h:mm A')}
                                </div>
                                {shift.employeeNames && (
                                    <div className="shift-map-sidebar-item-sub">
                                        <i className="fas fa-user" style={{ marginRight: 4, fontSize: 10 }}></i>
                                        {shift.employeeNames}
                                    </div>
                                )}
                                {shift.customerNames && (
                                    <div className="shift-map-sidebar-item-sub">
                                        <i className="fas fa-user-injured" style={{ marginRight: 4, fontSize: 10 }}></i>
                                        {shift.customerNames}
                                    </div>
                                )}
                                <div className={`shift-map-sidebar-item-status ${shift.missingCheckin ? 'missing' : 'checked-in'}`}>
                                    <i className={shift.missingCheckin ? 'fas fa-exclamation-circle' : 'fas fa-check-circle'} style={{ fontSize: 10 }}></i>
                                    {shift.missingCheckin ? 'Missing Check-in' : 'Checked In'}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
